import * as React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { ConfigurationScope, Settings, GeneralSettings, PastOrdersInfo, BusinessUser } from '../Model';
import { General, Invitations, PastOrders, Update, ProductReviews, Faq, Settings as SettingsTab, EnhancedPastOrders } from '.';
import { AuthComponent, Account } from '@trustpilot/ecommscript-integrationappauth';
import { SettingsRestore } from './SettingsRestore';
import { trackLink, trackButton } from '../Tracking';
import { FirebaseConfiguration } from '../Configuration/FirebaseConfiguration';

interface MainProps {
    settings: Settings;
    onChange: (settings: Settings) => void;
    sendPostMessage: (message: string | number | object) => void;
    integrationKey: string;
    loggedIn: boolean;
    forceUpdate: boolean;
    onLoggedIn: (toggle: boolean) => void;
    showUpdateNotification: boolean;
    onShowUpdateNotificationClose: () => void;
    showSettingsRestore: boolean;
    onRestoreSettings: (restore: boolean) => void;
    configurationScopeTree?: ConfigurationScope[];
    businessInformation?: Account;
    tierName: string;
    businessUser: BusinessUser;
    mode?: string;
    onConsent: (toggle: boolean) => void;
}

export class Main extends React.Component<MainProps> {
    constructor(props: MainProps) {
        super(props);
        this.onGeneralSettingsChange = this.onGeneralSettingsChange.bind(this);
        this.onPastOrdersSettingChange = this.onPastOrdersSettingChange.bind(this);
        this.onProductReviewsSettingChange = this.onProductReviewsSettingChange.bind(this);
        this.onSettingsSave = this.onSettingsSave.bind(this);
    }

    public onGeneralSettingsChange(generalSettings: GeneralSettings) {
        this.props.onChange({ general: generalSettings });
    }

    public onPastOrdersSettingChange(pastOrdersSettings: PastOrdersInfo) {
        this.props.onChange({ pastOrders: pastOrdersSettings });
    }

    public onProductReviewsSettingChange(selector: Settings) {
        this.props.onChange(selector);
    }

    public onSettingsSave() {
        this.props.sendPostMessage('submit');
    }

    public render() {
        const { settings, loggedIn, integrationKey, onLoggedIn, forceUpdate, sendPostMessage, showUpdateNotification,
                onShowUpdateNotificationClose, showSettingsRestore, onRestoreSettings, configurationScopeTree,
                businessInformation, tierName, businessUser, mode, onConsent } = this.props;
        const { general, pluginVersion, source, pastOrders, version, isFromMarketplace, shop } = settings;
        const renderGeneral = () => (
            <General
                settings={settings}
                onChange={this.onGeneralSettingsChange}
                onLoggedInChange={onLoggedIn}
                onSettingsSave={this.onSettingsSave}
                source={settings.source!}
                showUpdateNotification={showUpdateNotification}
                onShowUpdateNotificationClose={onShowUpdateNotificationClose}
                tierName={tierName}
                onProductReviewsSettingChange={this.onProductReviewsSettingChange}
                onConsentChange={onConsent}
            />
        );
        const renderSettingsRestore = () => (
            <SettingsRestore onRestoreSettings={onRestoreSettings}/>
        );
        const renderInvitations = () => !loggedIn
            ? (
                <AuthComponent
                    onLoggedIn={this.props.onLoggedIn}
                    source={settings.source!}
                    draft={businessInformation}
                    forgotPasswordUrl={process.env.REACT_APP_ForgotPasswordUrl!}
                    createAccountUrl={process.env.REACT_APP_CreateAccountUrl!}
                    cookiesDomain={process.env.REACT_APP_CookiesDomain!}
                    authenticationApiUrl={process.env.REACT_APP_AuthenticationApiUrl!}
                    simplicityUrl={process.env.REACT_APP_SimplicityUrl!}
                    settingsApiUrl={process.env.REACT_APP_SettingsApiUrl!}
                    locale={'en-US'}
                    integrationConfig={{}}
                    trackLink={trackLink}
                    trackButton={trackButton}
                    firebaseOptions={FirebaseConfiguration}
                />
            )
            : (
                <Invitations
                    configurationScopeTree={configurationScopeTree}
                    integrationKey={integrationKey}
                    onLoggedInChange={onLoggedIn}
                    source={settings.source!}
                    invitationTrigger={general!.invitationTrigger!}
                    onChange={this.onGeneralSettingsChange}
                    onSettingsSave={this.onSettingsSave}
                    tierName={tierName}
                    onConsentChange={onConsent}
                />
            );
        const renderPastOrders = () => (
            settings.source!.toLowerCase() === 'shopify' ?
            <EnhancedPastOrders
                onProductReviewsSettingChange={this.onProductReviewsSettingChange}
                onSettingsSave={this.onSettingsSave}
                sendPostMessage={this.props.sendPostMessage}
            /> :
            <PastOrders
                sendPostMessage={this.props.sendPostMessage}
                syncedOrders={pastOrders!.synced!}
                unsyncedOrders={pastOrders!.unsynced!}
                failedOrders={pastOrders!.failed!}
                showInitial={pastOrders!.showInitial}
                totalOrders={pastOrders!.total}
                syncInProgress={pastOrders!.syncInProgress}
                onChange={this.onPastOrdersSettingChange}
                source={settings.source!}
                tierName={tierName}
                integrationKey={integrationKey}
            />
        );
        const renderProductReviews = () => !loggedIn
            ? (
                <AuthComponent
                    onLoggedIn={this.props.onLoggedIn}
                    source={settings.source!}
                    draft={businessInformation}
                    forgotPasswordUrl={process.env.REACT_APP_ForgotPasswordUrl!}
                    createAccountUrl={process.env.REACT_APP_CreateAccountUrl!}
                    cookiesDomain={process.env.REACT_APP_CookiesDomain!}
                    authenticationApiUrl={process.env.REACT_APP_AuthenticationApiUrl!}
                    simplicityUrl={process.env.REACT_APP_SimplicityUrl!}
                    settingsApiUrl={process.env.REACT_APP_SettingsApiUrl!}
                    locale={'en-US'}
                    integrationConfig={{}}
                    trackLink={trackLink}
                    trackButton={trackButton}
                    firebaseOptions={FirebaseConfiguration}
                />
            )
            : (
                <ProductReviews
                    integrationKey={this.props.integrationKey}
                    onLoggedInChange={this.props.onLoggedIn}
                    onProductReviewsSettingChange={this.onProductReviewsSettingChange}
                    onSettingsSave={this.onSettingsSave}
                    source={settings.source!}
                    gtin={settings.gtinSelector}
                    mpn={settings.mpnSelector}
                    sku={settings.skuSelector}
                    productIdentificationOptions={settings.productIdentificationOptions}
                    sendPostMessage={sendPostMessage}
                    tierName={tierName}
                    businessUser={businessUser}
                    shop={shop}
                    onConsentChange={onConsent}
                />
            );
        const renderUpdate = () => (
            <Update
                version={version!}
                pluginVersion={pluginVersion!}
                source={source!}
                sendPostMessage={sendPostMessage}
                isFromMarketplace={isFromMarketplace}
                tierName={tierName}
            />
        );
        const renderFaq = () => <Faq source={source!} integrationKey={this.props.integrationKey!}/>;
        const renderSettings = () => (
            <SettingsTab
                settings={general!}
                onChange={this.onGeneralSettingsChange}
                onSettingsSave={this.onSettingsSave}
                source={settings.source!}
                tierName={tierName}
            />
        );

        const shouldRedirectToTrustBox = !forceUpdate && mode === 'trustbox-only' && location.pathname !== '/trustbox';

        return (
            <Switch>
                <Route path="/general" render={showSettingsRestore ? renderSettingsRestore : renderGeneral} />
                <Route path="/invitations" render={renderInvitations} />
                <Route path="/past-orders" render={renderPastOrders} />
                <Route path="/faq" render={renderFaq} />
                <Route path="/update" render={renderUpdate} />
                <Route path="/product-reviews" render={renderProductReviews} />
                <Route path="/settings" render={renderSettings} />
                <Redirect to={shouldRedirectToTrustBox ? '/trustbox' : '/general'} />
            </Switch>
        );
    }
}



// WEBPACK FOOTER //
// ./src/Containers/Main.tsx